import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Table, Progress, Spin, Descriptions, Tag } from 'antd';
import {
  UserOutlined,
  DollarOutlined,
  RiseOutlined,
  TeamOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  PhoneOutlined,
  MailOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { getDashboardStats, getUserProfile, getPromotionStats } from '../services/adminApi';
import { APP_CONFIG } from '../config';

interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  totalRevenue: number;
  totalProfit: number;
  userGrowth: string;
  roleStats: Array<{ role: string; count: number }>;
  latestUsers: Array<{
    id: number;
    phone: string;
    nickname: string;
    role: string;
    status: string;
    createdAt: string;
  }>;
  revenueTrend: Array<{ name: string; revenue: number; profit: number }>;
}

interface UserProfile {
  id: number;
  phone: string;
  nickname: string;
  role: string;
  status: string;
  referralCode: string;
  createdAt: string;
  profile?: {
    companyName?: string;
    contactPerson?: string;
    contactPhone?: string;
  };
}

interface PromotionStats {
  totalStats: {
    total: number;
    visited: number;
    registered: number;
    approved: number;
  };
  todayStats: {
    total: number;
    visited: number;
    registered: number;
    approved: number;
  };
  monthStats: {
    total: number;
    visited: number;
    registered: number;
    approved: number;
  };
  recentVisitors: Array<{
    id: number;
    phone: string;
    visitTime: string;
    registerTime: string | null;
    status: string;
  }>;
}

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [promotionStats, setPromotionStats] = useState<PromotionStats | null>(null);
  const [userRole, setUserRole] = useState<string>('');

  // 角色映射
  const roleMap: Record<string, string> = {
    admin: '管理员',
    level1: '一级推荐官',
    level2: '二级推荐官',
    level3: '三级推荐官',
    retail: '用电用户',
    regional: '区域代理'
  };

  // 角色颜色映射
  const roleColorMap: Record<string, string> = {
    '用电用户': '#1890ff',
    '一级推荐官': '#52c41a',
    '二级推荐官': '#faad14',
    '三级推荐官': '#722ed1',
    '区域代理': '#eb2f96',
    '管理员': '#f5222d'
  };

  useEffect(() => {
    const userStr = localStorage.getItem(APP_CONFIG.userKey);
    if (userStr) {
      const user = JSON.parse(userStr);
      setUserRole(user.role);
      fetchDashboardData(user.role);
    }
  }, []);

  const fetchDashboardData = async (role: string) => {
    try {
      setLoading(true);

      if (role === 'admin') {
        const response = await getDashboardStats();
        if (response.data.success) {
          setStats(response.data.data);
        }
      } else {
        // 非管理员角色获取个人数据和推广统计
        const [profileRes, promotionRes] = await Promise.all([
          getUserProfile(),
          getPromotionStats().catch(() => null)
        ]);

        if (profileRes.data.success) {
          setUserProfile(profileRes.data.data);
        }

        if (promotionRes && promotionRes.data.success) {
          setPromotionStats(promotionRes.data.data);
        }
      }
    } catch (error) {
      console.error('获取仪表盘数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: '用户名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <span style={{ color: status === '活跃' ? '#52c41a' : '#f5222d' }}>
          {status}
        </span>
      ),
    },
    {
      title: '加入时间',
      dataIndex: 'joinDate',
      key: 'joinDate',
    },
  ];

  const visitorColumns = [
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: '访问时间',
      dataIndex: 'visitTime',
      key: 'visitTime',
      render: (time: string) => new Date(time).toLocaleString(),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap: Record<string, { text: string; color: string }> = {
          visited: { text: '已访问', color: '#1890ff' },
          registered: { text: '已注册', color: '#52c41a' },
          approved: { text: '已审核', color: '#722ed1' }
        };
        const s = statusMap[status] || { text: status, color: '#999' };
        return <Tag color={s.color}>{s.text}</Tag>;
      },
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  // 管理员视图
  if (userRole === 'admin' && stats) {
    const userDistribution = stats.roleStats.map(item => ({
      name: roleMap[item.role] || item.role,
      value: item.count,
      color: roleColorMap[roleMap[item.role]] || '#1890ff'
    }));

    const latestUsers = stats.latestUsers.map(user => ({
      key: user.id,
      name: user.nickname || user.phone,
      role: roleMap[user.role] || user.role,
      status: user.status === 'active' ? '活跃' : '非活跃',
      joinDate: new Date(user.createdAt).toLocaleDateString(),
    }));

    const userGrowth = parseFloat(stats.userGrowth);

    return (
      <div>
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} lg={6}>
            <Card className="card-stat">
              <Statistic
                title="总用户数"
                value={stats.totalUsers}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#3f8600' }}
                suffix={
                  <span style={{ fontSize: '14px', color: userGrowth >= 0 ? '#3f8600' : '#cf1322' }}>
                    {userGrowth >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {Math.abs(userGrowth)}%
                  </span>
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="card-stat">
              <Statistic
                title="活跃用户"
                value={stats.activeUsers}
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="card-stat">
              <Statistic
                title="总收入"
                value={stats.totalRevenue}
                prefix={<DollarOutlined />}
                precision={0}
                valueStyle={{ color: '#cf1322' }}
                suffix="元"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="card-stat">
              <Statistic
                title="总利润"
                value={stats.totalProfit}
                prefix={<RiseOutlined />}
                precision={0}
                valueStyle={{ color: '#722ed1' }}
                suffix="元"
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} lg={16}>
            <Card title="收益趋势" className="dashboard-chart">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={stats.revenueTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="revenue" stroke="#1890ff" strokeWidth={2} name="收入" />
                  <Line type="monotone" dataKey="profit" stroke="#52c41a" strokeWidth={2} name="利润" />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card title="用户分布" className="dashboard-chart">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={userDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {userDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="角色分布统计">
              {userDistribution.map((item, index) => (
                <div key={index} style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span>{item.name}</span>
                    <span>{item.value}人</span>
                  </div>
                  <Progress
                    percent={(item.value / stats.totalUsers) * 100}
                    strokeColor={item.color}
                    showInfo={false}
                  />
                </div>
              ))}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="最新用户">
              <Table
                dataSource={latestUsers}
                columns={columns}
                rowKey="key"
                pagination={false}
                size="small"
              />
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  // 非管理员视图
  return (
    <div>
      {userProfile && (
        <Card title="个人信息" style={{ marginBottom: '24px' }}>
          <Descriptions column={{ xs: 1, sm: 2, md: 3 }}>
            <Descriptions.Item label="昵称">{userProfile.nickname || '未设置'}</Descriptions.Item>
            <Descriptions.Item label="手机号">{userProfile.phone}</Descriptions.Item>
            <Descriptions.Item label="角色">
              <Tag color={roleColorMap[roleMap[userProfile.role]]}>{roleMap[userProfile.role]}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={userProfile.status === 'active' ? '#52c41a' : '#f5222d'}>
                {userProfile.status === 'active' ? '活跃' : '非活跃'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="推荐码">{userProfile.referralCode}</Descriptions.Item>
            <Descriptions.Item label="注册时间">
              {new Date(userProfile.createdAt).toLocaleString()}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {promotionStats && (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} lg={6}>
              <Card className="card-stat">
                <Statistic
                  title="总访问"
                  value={promotionStats?.totalStats?.total || 0}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="card-stat">
                <Statistic
                  title="今日访问"
                  value={promotionStats?.todayStats?.total || 0}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="card-stat">
                <Statistic
                  title="已注册"
                  value={promotionStats?.totalStats?.registered || 0}
                  prefix={<RiseOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="card-stat">
                <Statistic
                  title="已审核"
                  value={promotionStats?.totalStats?.approved || 0}
                  prefix={<DollarOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
          </Row>

          <Card title="最近访客">
            <Table
              dataSource={promotionStats?.recentVisitors?.map(v => ({
                key: v.id,
                phone: v.phone,
                visitTime: v.visitTime,
                status: v.status
              })) || []}
              columns={visitorColumns}
              rowKey="key"
              pagination={{ pageSize: 10 }}
              size="small"
            />
          </Card>
        </>
      )}

      {!userProfile && !promotionStats && (
        <Card>
          <p>暂无数据</p>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;